import { SearchOutlined } from '@ant-design/icons';
import { ConfigProvider, Input, Modal, Pagination, theme } from 'antd';
import React, { useEffect, useState } from 'react';

import { getOrganizationActivityPaginated } from '@/services/Organization';

interface SpendingActivityModalProps {
  open: boolean;
  onClose: () => void;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || 'EM';
};

const getAvatarBg = (name: string) => {
  const colors = ['#2D3748', '#4A5568', '#3B82F6', '#6366F1', '#8B5CF6', '#10B981', '#F59E0B'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const SpendingActivityModal: React.FC<SpendingActivityModalProps> = ({
  open,
  onClose,
}) => {
  const [search, setSearch] = useState('');
  const [activities, setActivities] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  const fetchActivities = async (pageNumber: number) => {
    setLoading(true);
    try {
      const res = await getOrganizationActivityPaginated(pageNumber);
      const list = res?.activities || [];
      setActivities(list);
      if (typeof res?.total === 'number') {
        setTotal(res.total);
      } else {
        setTotal(list.length);
      }
      if (res?.per_page) {
        setPageSize(res.per_page);
      }
      if (res?.current_page) {
        setPage(res.current_page);
      }
    } catch {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSearch('');
      setPage(1);
      fetchActivities(1);
    }
  }, [open]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchActivities(newPage);
  };

  const filtered = activities.filter((t) => {
    const name = (
      t.member?.user?.name ||
      t.member?.invited_name ||
      t.user_name ||
      t.name ||
      ''
    ).toLowerCase();
    const title = (
      t.order?.order_details?.[0]?.product?.name ||
      t.order?.order_details?.[0]?.name ||
      t.title ||
      t.bookTitle ||
      t.description ||
      ''
    ).toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || title.includes(q);
  });

  return (
    <ConfigProvider
      theme={{
        algorithm: [theme.darkAlgorithm],
        token: {
          colorPrimary: '#FF5612',
          colorBgElevated: '#0F0F0F',
          colorBorder: '#262626',
          borderRadius: 12,
        },
      }}
    >
      <Modal
        title={
          <div>
            <h3 className='text-lg font-bold text-white tracking-tight'>
              All Spending Activity
            </h3>
            <p className='text-xs text-grayZl-400 font-normal mt-0.5'>
              Detailed breakdown of books and playbooks purchased with corporate balance.
            </p>
          </div>
        }
        open={open}
        onCancel={onClose}
        footer={null}
        width={680}
        styles={{
          body: {
            padding: '16px 0',
          },
        }}
      >
        <div className='px-6 mb-4'>
          <Input
            prefix={<SearchOutlined className='text-grayZl-500 mr-1' />}
            placeholder='Search by employee or book title...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='!bg-[#161616] !border-[#2A2A2A] !text-white !h-10 !rounded-lg'
          />
        </div>

        <div className='min-h-[360px] max-h-[440px] overflow-y-auto overflow-x-hidden px-6 divide-y divide-[#1C1C1C]'>
          {loading ? (
            <div className='divide-y divide-[#1C1C1C]'>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className='py-4 flex items-center justify-between animate-pulse'>
                  <div className='flex items-center gap-3.5 min-w-0 flex-1 mr-4'>
                    <div className='w-9 h-9 min-w-[36px] min-h-[36px] rounded-full bg-[#1C1C1C] shrink-0' />
                    <div className='space-y-2 min-w-0 flex-1'>
                      <div className='h-3.5 bg-[#1C1C1C] rounded w-36 max-w-full' />
                      <div className='h-2.5 bg-[#1C1C1C] rounded w-52 max-w-full' />
                    </div>
                  </div>
                  <div className='h-4 bg-[#1C1C1C] rounded w-16 shrink-0' />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className='py-16 text-center'>
              <p className='text-sm font-medium text-grayZl-400'>
                {search ? 'No activities match your search.' : 'No spending activity found.'}
              </p>
              {search && (
                <button
                  type='button'
                  onClick={() => setSearch('')}
                  className='mt-2 text-xs font-semibold text-[#FF5612] hover:underline cursor-pointer'
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            filtered.map((item, idx) => {
              const name =
                item.member?.user?.name ||
                item.member?.invited_name ||
                item.user_name ||
                item.name ||
                'Team Member';
              const title =
                item.order?.order_details?.[0]?.product?.name ||
                item.order?.order_details?.[0]?.name ||
                item.title ||
                item.description ||
                (item.transaction_type === 'product_purchase'
                  ? 'Product Purchase'
                  : item.transaction_type === 'wallet_recharge'
                  ? 'Wallet Top-up'
                  : 'Learning Playbook');
              const date =
                item.time_ago ||
                (item.created_at
                  ? new Date(item.created_at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Recent');
              const rawAmount = item.amount ?? item.inr_amount ?? 499;
              const amount = Number(rawAmount) || 0;
              const isCredit =
                item.type === 'credit' || item.transaction_type === 'wallet_recharge';

              return (
                <div key={item.id || idx} className='py-3.5 flex items-center justify-between first:pt-1 last:pb-2'>
                  <div className='flex items-center gap-3.5 min-w-0 flex-1 mr-4'>
                    <div
                      className='w-9 h-9 min-w-[36px] min-h-[36px] max-w-[36px] max-h-[36px] rounded-full flex items-center justify-center font-bold text-white text-xs shrink-0 select-none shadow-sm'
                      style={{ backgroundColor: getAvatarBg(name) }}
                    >
                      {getInitials(name)}
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='text-xs sm:text-sm font-semibold text-white leading-tight truncate'>
                        {name}
                      </p>
                      <p className='text-[11px] text-grayZl-400 mt-0.5 truncate'>{title}</p>
                      <p className='text-[10px] text-grayZl-500 mt-0.5'>{date}</p>
                    </div>
                  </div>

                  <div className='text-right shrink-0 ml-3'>
                    <span
                      className={`text-sm font-bold ${
                        isCredit ? 'text-[#4ADE80]' : 'text-[#FF5612]'
                      }`}
                    >
                      {isCredit ? '+' : '-'}₹{(Number(amount) || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Ant Design Pagination UI */}
        {!loading && total > pageSize && (
          <div className='pt-4 pb-1 px-6 border-t border-[#1C1C1C] flex items-center justify-between'>
            <span className='text-xs text-grayZl-500'>
              Showing {Math.min((page - 1) * pageSize + 1, total)} -{' '}
              {Math.min(page * pageSize, total)} of {total} activities
            </span>
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              size='small'
              className='dark-pagination'
            />
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default SpendingActivityModal;
